import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, ShoppingBag, ShoppingCart, Globe } from 'lucide-react'; // J'ai retiré 'Zap' car on ne l'utilise plus
import { theme } from '../../utils/theme';
import { useLanguage } from '../../contexts/LanguageContext';

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const { language, switchLanguage, t } = useLanguage();

  useEffect(() => {
    const updateCount = () => {
      const cart = JSON.parse(localStorage.getItem('sailec_cart') || '[]');
      const count = Array.isArray(cart) ? cart.reduce((acc, item) => acc + (item.quantity || 1), 0) : 0;
      setCartCount(count);
    };

    updateCount();
    window.addEventListener('storage', updateCount);
    return () => window.removeEventListener('storage', updateCount);
  }, []);

  return (
    <nav 
      className="fixed w-full z-50 backdrop-blur-xl border-b transition-all duration-300"
      style={{ backgroundColor: 'rgba(5, 5, 5, 0.7)', borderColor: theme.colors.border }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* --- LOGO CLIQUABLE VERS "/" --- */}
          {/* 2. On utilise <Link to="/"> au lieu d'une div avec onClick */}
          <Link 
            to="/" 
            className="flex items-center gap-4 cursor-pointer select-none group"
            onClick={() => window.scrollTo(0, 0)} /* Optionnel : remonte en haut de page au clic */
          >
            {/* Conteneur des formes géométriques */}
            <div className="relative w-10 h-10 transform transition-transform duration-500 group-hover:scale-110">
              
              {/* Cercle Orange */}
              <div 
                className="absolute top-0 left-0 w-[80%] h-[80%] rounded-full transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:-translate-x-0.5"
                style={{ 
                  backgroundColor: '#C88A4D', 
                  opacity: 0.9 
                }}
              />
              
              {/* Carré Teal */}
              <div 
                className="absolute bottom-0 right-0 w-[70%] h-[70%] transition-transform duration-500 group-hover:translate-y-0.5 group-hover:translate-x-0.5"
                style={{ 
                  backgroundColor: '#9DCCC3', 
                  opacity: 0.9 
                }}
              />
            </div>

            {/* Texte */}
            <div className="flex flex-col leading-none">
              <span className="text-xl font-bold tracking-wide text-white group-hover:tracking-widest transition-all duration-300">
                SAILEC
              </span>
              <span 
                className="text-xs font-bold tracking-[0.25em] uppercase" 
                style={{ color: theme.colors.primary }}
              >
                tech
              </span>
            </div>
          </Link>
          {/* --- FIN SECTION LOGO MODIFIÉE --- */}
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="relative text-sm font-medium transition-colors hover:text-white group" style={{ color: theme.colors.textMuted }}>
              {t.nav.home}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full" />
            </Link>
            <Link to="/solution" className="relative text-sm font-medium transition-colors hover:text-white group" style={{ color: theme.colors.textMuted }}>
              {t.nav.solution}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full" />
            </Link>
            <Link to="/about" className="relative text-sm font-medium transition-colors hover:text-white group" style={{ color: theme.colors.textMuted }}>
              {t.nav.about}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full" />
            </Link>
            
            {/* Language Switcher */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: theme.colors.surface }}>
              <Globe size={16} style={{ color: theme.colors.textMuted }} />
              <button
                onClick={() => switchLanguage('fr')}
                className={`px-2 py-1 rounded text-xs font-bold transition-all ${language === 'fr' ? 'text-black' : 'text-gray-500 hover:text-white'}`}
                style={{ backgroundColor: language === 'fr' ? theme.colors.primary : 'transparent' }}
              >
                FR
              </button>
              <button
                onClick={() => switchLanguage('en')}
                className={`px-2 py-1 rounded text-xs font-bold transition-all ${language === 'en' ? 'text-black' : 'text-gray-500 hover:text-white'}`}
                style={{ backgroundColor: language === 'en' ? theme.colors.primary : 'transparent' }}
              >
                EN
              </button>
            </div>
            
            {/* Shop Icon */}
            <button 
              onClick={() => navigate('/shop')}
              className="relative p-3 rounded-full transition-all duration-300 hover:bg-white/10 hover:scale-110"
              style={{ color: theme.colors.primary }}
            >
              <ShoppingBag size={24} />
            </button>

            {/* Cart Page Icon */}
            <button 
              onClick={() => navigate('/cart')}
              className="relative p-3 rounded-full transition-all duration-300 hover:bg-white/10 hover:scale-110"
              style={{ color: theme.colors.white }}
            >
              <ShoppingCart size={24} />
              {cartCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: theme.colors.danger, color: theme.colors.white }}
                >
                  {cartCount}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex md:hidden">
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              className="p-2 text-white transition-transform active:scale-90"
            >
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Dropdown */}
      <div 
        className={`md:hidden overflow-hidden transition-all duration-500 ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        } border-b bg-black/95`} 
        style={{ borderColor: theme.colors.border }}
      >
        <div className="px-4 py-4 space-y-3">
          <button 
            onClick={() => { navigate('/'); setIsMenuOpen(false); }}
            className="block text-lg font-medium py-2 border-b border-white/5 w-full text-left"
          >
            {t.nav.home}
          </button>
          <button 
            onClick={() => { navigate('/solution'); setIsMenuOpen(false); }}
            className="block text-lg font-medium py-2 border-b border-white/5 w-full text-left"
          >
            {t.nav.solution}
          </button>
          <button 
            onClick={() => { navigate('/about'); setIsMenuOpen(false); }}
            className="block text-lg font-medium py-2 border-b border-white/5 w-full text-left"
          >
            {t.nav.about}
          </button>
          
          {/* Language Switcher Mobile */}
          <div className="flex items-center gap-2 py-2">
            <Globe size={16} style={{ color: theme.colors.textMuted }} />
            <button
              onClick={() => switchLanguage('fr')}
              className={`px-3 py-1.5 rounded text-sm font-bold transition-all ${language === 'fr' ? 'text-black' : 'text-gray-500'}`}
              style={{ backgroundColor: language === 'fr' ? theme.colors.primary : theme.colors.surface }}
            >
              FR
            </button>
            <button
              onClick={() => switchLanguage('en')}
              className={`px-3 py-1.5 rounded text-sm font-bold transition-all ${language === 'en' ? 'text-black' : 'text-gray-500'}`}
              style={{ backgroundColor: language === 'en' ? theme.colors.primary : theme.colors.surface }}
            >
              EN
            </button>
          </div>
          
          <button 
            onClick={() => navigate('/shop')}
            className="w-full py-3 mt-4 rounded-xl font-bold text-black" 
            style={{ backgroundColor: theme.colors.primary }}
          >
            {t.hero.buyButton}
          </button>
        </div>
      </div>
    </nav>
  );
};